import { useState } from 'react';
import { useGuild } from '../contexts/GuildContext';
import { useToast } from '../contexts/ToastContext';
import { useWhitelist, useAddWhitelistEntry, useRemoveWhitelistEntry } from '../hooks/useWhitelist';
import { useChannels, useRoles, useMembers } from '../hooks/usePunishments';
import { getErrorMessage } from '../api/errorHandler';
import DashboardLayout from '../components/DashboardLayout';
import ServerSelector from '../components/ServerSelector';
import WhitelistTab from '../components/WhitelistTab';

type TabType = 'channels' | 'roles' | 'users';

export default function WhitelistPage() {
  const { selectedGuildId, setSelectedGuildId } = useGuild();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('channels');

  const { data: whitelist, isLoading: whitelistLoading } = useWhitelist(selectedGuildId);
  const { data: channels = [], isLoading: channelsLoading } = useChannels(selectedGuildId);
  const { data: roles = [], isLoading: rolesLoading } = useRoles(selectedGuildId);
  const { data: members = [], isLoading: membersLoading } = useMembers(selectedGuildId);

  const addEntry = useAddWhitelistEntry(selectedGuildId);
  const removeEntry = useRemoveWhitelistEntry(selectedGuildId);

  const handleAdd = async (type: TabType, id: string) => {
    try {
      await addEntry.mutateAsync({ type, id });
      showToast('success', '항목이 추가되었습니다');
    } catch (error) {
      showToast('error', getErrorMessage(error));
    }
  };

  const handleRemove = async (type: TabType, id: string) => {
    if (!confirm('이 항목을 삭제하시겠습니까?')) return;

    try {
      await removeEntry.mutateAsync({ type, id });
      showToast('success', '항목이 삭제되었습니다');
    } catch (error) {
      showToast('error', getErrorMessage(error));
    }
  };

  const tabs = [
    { id: 'channels' as TabType, label: '채널', icon: '📢' },
    { id: 'roles' as TabType, label: '역할', icon: '🎭' },
    { id: 'users' as TabType, label: '사용자', icon: '👤' },
  ];

  const getAvailableOptions = (type: TabType) => {
    switch (type) {
      case 'channels':
        return channels.map((c) => ({ id: c.id, name: c.name }));
      case 'roles':
        return roles.map((r) => ({ id: r.id, name: r.name }));
      case 'users':
        return members.map((m) => ({
          id: m.id,
          name: m.nick || `${m.username}#${m.discriminator}`,
        }));
    }
  };

  const getPlaceholder = (type: TabType) => {
    switch (type) {
      case 'channels':
        return '채널 이름으로 검색...';
      case 'roles':
        return '역할 이름으로 검색...';
      case 'users':
        return '사용자 이름으로 검색...';
    }
  };

  const isLoading =
    whitelistLoading ||
    (activeTab === 'channels' && channelsLoading) ||
    (activeTab === 'roles' && rolesLoading) ||
    (activeTab === 'users' && membersLoading);

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">화이트리스트</h1>
              <p className="text-gray-600">필터링에서 제외할 채널, 역할, 사용자를 관리하세요</p>
            </div>
            <div className="mt-4 md:mt-0 md:w-80">
              <ServerSelector
                selectedGuildId={selectedGuildId}
                onSelectGuild={setSelectedGuildId}
              />
            </div>
          </div>

          {!selectedGuildId ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-700">서버를 선택해주세요</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-[#FF7A7A] text-[#FF7A7A]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                  </div>
                ) : (
                  <WhitelistTab
                    entries={whitelist?.[activeTab] || []}
                    availableOptions={getAvailableOptions(activeTab)}
                    onAdd={(id) => handleAdd(activeTab, id)}
                    onRemove={(id) => handleRemove(activeTab, id)}
                    isLoading={addEntry.isPending || removeEntry.isPending}
                    placeholder={getPlaceholder(activeTab)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
